import React, { useRef, useEffect } from "react";
import { useVesselContext, Vessel } from "../../contexts/VesselContext";
import { useMapDisplayContext } from "../../contexts/MapDisplayContext";
import L from "leaflet";
import 'leaflet.markercluster';
import 'leaflet-path-drag'
import 'leaflet-rotatedmarker';
import '../../libs/Leaflet.VectorGrid.bundled.js';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import styles from "./MapContainer.module.css";
import { apiKey } from "../../constants/Api";
import { mapBounds, tileLayerStyles, borders, rankZoom } from "../../constants/Map";
import { portList } from "../../constants/Port";
import { vesselColor } from "../../constants/Vessel";
import { useWarningZoneContext, WarningZone } from "../../contexts/WarningZoneContext";
import ReactDOMServer from "react-dom/server";
import ReactDOM from "react-dom/client";
import VesselMarkerPopup from "../MarkerPopup/VesselMarkerPopup";
import VesselMarkerIcon from "../Icons/VesselMarkerIcon";
import PulseEffectIcon from "../Icons/PulseEffectIcon";
import portBlue from "../../assets/icons/marker-icon-blue.png";
import portYellow from "../../assets/icons/marker-icon-yellow.png";
import { SidebarContentType, useSidebarContext } from "../../contexts/SidebarContext";
import { positionIcon } from "../../constants/Position";
import WarningZonePopup from "../MarkerPopup/WarningZoneMarkerPopup";

// TypeScript declaration for Leaflet.VectorGrid
declare module "leaflet" {
    namespace vectorGrid {
        function protobuf(url: string, options?: any): any;
    }
}

// Extend the Window interface to include windyInit
declare global {
    interface Window {
        windyInit?: any;
    }
}
const MapContainer: React.FC = () => {
    const { mapDisplay, setAction, zoomLevel, setZoomLevel } = useMapDisplayContext();
    const leafletMapRef = useRef<L.Map | null>(null);
    const windyMapRef = useRef<L.Map | null>(null);
    const windyMapStoreRef = useRef<any>(null);
    const currentMapRef = useRef<L.Map | null>(null);
    const leafletClusterGroup = useRef<L.MarkerClusterGroup>(L.markerClusterGroup({
        showCoverageOnHover: false,  // không hiển thị đường viền khi rê chuột
        maxClusterRadius: 50,        // bán kính gom nhóm
        disableClusteringAtZoom: 12  // dừng gom nhóm khi zoom >= 12
    }));
    const windyClusterGroup = useRef<L.MarkerClusterGroup>(L.markerClusterGroup({
        showCoverageOnHover: false,  // không hiển thị đường viền khi rê chuột
        maxClusterRadius: 50,        // bán kính gom nhóm
        disableClusteringAtZoom: 12  // dừng gom nhóm khi zoom >= 12
    }));
    const warningZoneLeafletLayerGroup = useRef<L.LayerGroup>(L.layerGroup());
    const warningZoneWindyLayerGroup = useRef<L.LayerGroup>(L.layerGroup());
    const trackPath = useRef<L.Polyline>(L.polyline([]));
    const trackPointIconLayer = useRef<L.LayerGroup>(L.layerGroup());
    const { vessels, currentPositionDataRange } = useVesselContext();
    const { warningZones } = useWarningZoneContext();
    const vesselMarkerIcon: string = ReactDOMServer.renderToString(<VesselMarkerIcon />);
    const previousVesselsRef = useRef(vessels);
    const { setDraggableCirclePosition, setChosenWarningZoneId, chosenWarningZoneId: warningZoneBeingEditedId, contentType, vesselBeingTrackedId, setChatBeingViewedId, setVesselBeingTrackedId, openSidebar, chosenPosition, setChosenPosition } = useSidebarContext();
    const highlightIcon = useRef<L.CircleMarker>((L.circleMarker(mapBounds.getCenter(), {
        fillColor: '#ffffffff',
        color: '#ff0000',
        fillOpacity: 1,
        radius: 12,
    })));
    const draggableCircle = useRef<L.Circle>(
        L.circle(L.latLng(0, 0), {
            color: 'orange',
            fillColor: '#ffa500',
            fillOpacity: 0.3,
            radius: 150000,
            draggable: true,
            weight: 0.1,
        } as any)
    );
    //khởi tạo bản đồ khi thay đổi mapDisplay
    useEffect(() => {
        if (mapDisplay == "windy") {
            document.getElementById("windy")!.style.display = "block";
            document.getElementById("leaflet")!.style.display = "none";
            if (!windyMapRef.current) {
                // Tạo script tag để tải Windy lib nếu chưa có
                if (!document.getElementById("windy-lib-script")) {
                    const script = document.createElement("script");
                    script.src = "https://api.windy.com/assets/map-forecast/libBoot.js";
                    script.async = true;
                    script.id = "windy-lib-script";
                    script.onload = () => {
                        if (window.windyInit) {
                            const options = {
                                key: apiKey.WindyMapForecast,
                                verbose: false,
                                lat: 21.028511,
                                lon: 105.854444,
                                zoom: zoomLevel,
                            };
                            window.windyInit(options, (windyAPI: any) => {
                                //set các thuộc tính map từ windyAPI
                                windyAPI.map.setMinZoom(5);
                                windyAPI.map.setMaxZoom(20);
                                windyAPI.map.setMaxBounds(mapBounds);
                                windyAPI.map.options.maxBoundsViscosity = 1;//độ kéo ra xa khỏi khu vực MaxBounds
                                windyAPI.map.attributionControl.remove();//xoá biểu tượng leaflet
                                // Đổi đơn vị gió sang km/h
                                windyAPI.overlays.wind.setMetric('km/h');
                                //dữ liệu thời tiết tại điểm được click
                                windyAPI.map.on('click', (e: any) => {
                                    windyAPI.picker.open({ lat: e.latlng.lat, lon: e.latlng.lng });
                                });
                                windyAPI.map.on('zoomend', () => {
                                    setZoomLevel(windyAPI.map.getZoom());
                                });
                                // windyAPI.map là instance Leaflet
                                initializeBaseMap(windyAPI.map, windyAPI.store, mapDisplay);
                                warningZoneWindyLayerGroup.current.addTo(windyAPI.map);
                                windyClusterGroup.current.addTo(windyAPI.map);
                                windyMapRef.current = windyAPI.map;
                                windyMapStoreRef.current = windyAPI.store;
                                currentMapRef.current = windyMapRef.current;
                            });
                        }
                    };
                    document.body.appendChild(script);
                }
            }
            currentMapRef.current = windyMapRef.current;
            // Hiển thị windy map, ẩn leaflet map
            currentMapRef.current?.setView(leafletMapRef.current!.getCenter(), leafletMapRef.current!.getZoom());

        } else if (mapDisplay == "leaflet") {
            document.getElementById("windy")!.style.display = "none";
            document.getElementById("leaflet")!.style.display = "block";
            if (!leafletMapRef.current) {
                leafletMapRef.current = L.map("leaflet", {
                    maxZoom: 20,
                    minZoom: 5,
                    attributionControl: false,
                    maxBounds: mapBounds,
                    maxBoundsViscosity: 1,
                    zoomControl: false,
                }).setView([21.028511, 105.854444], 6);
                L.control.zoom({ position: 'topright' }).addTo(leafletMapRef.current);
                leafletMapRef.current.on('zoomend', () => {
                    setZoomLevel(leafletMapRef.current!.getZoom());
                });
                // Khởi tạo các thành phần bản đồ cơ sở
                initializeBaseMap(leafletMapRef.current, null, mapDisplay);
                warningZoneLeafletLayerGroup.current.addTo(leafletMapRef.current);
                leafletClusterGroup.current.addTo(leafletMapRef.current);
            }
            currentMapRef.current = leafletMapRef.current;
            if (windyMapRef.current) {
                currentMapRef.current.setView(windyMapRef.current.getCenter(), windyMapRef.current.getZoom());
            }

        }
    }, [mapDisplay]);
    // khởi tạo marker vùng cảnh báo
    useEffect(() => {
        // Xóa các marker cũ khỏi cluster group
        warningZoneLeafletLayerGroup.current.clearLayers();
        warningZoneWindyLayerGroup.current.clearLayers();
        Object.keys(warningZones).forEach((key) => {
            if (warningZones[key].status == 3 || warningZones[key].endTime < Date.now()) return;
            warningZoneLeafletLayerGroup.current.addLayer(createWarningZoneCircle(warningZones[key], openSidebar, setChosenWarningZoneId));
            warningZoneWindyLayerGroup.current.addLayer(createWarningZoneCircle(warningZones[key], openSidebar, setChosenWarningZoneId));
        });
    }, [warningZones]);
    // khởi tạo marker tàu thuyền
    useEffect(() => {
        // Xóa các marker cũ khỏi cluster group
        leafletClusterGroup.current.clearLayers();
        windyClusterGroup.current.clearLayers();
        Object.keys(vessels).forEach((key) => {
            const vessel = vessels[key];
            const leafletMarker = createVesselMarker(key, vessel, vesselMarkerIcon, openSidebar, setVesselBeingTrackedId, setChatBeingViewedId);
            const windyMarker = createVesselMarker(key, vessel, vesselMarkerIcon, openSidebar, setVesselBeingTrackedId, setChatBeingViewedId);
            // Thêm marker vào cluster group
            leafletClusterGroup.current.addLayer(leafletMarker);
            windyClusterGroup.current.addLayer(windyMarker);
            if (previousVesselsRef.current[key] && (JSON.stringify(previousVesselsRef.current[key]) != JSON.stringify(vessel))) {
                pulseEffectOnVesselMarker(currentMapRef.current!, leafletMarker.getLatLng().lat, leafletMarker.getLatLng().lng);
            }
        });
        previousVesselsRef.current = vessels;
    }, [vessels]);
    // vẽ đường đi và các marker vị trí khi thay đổi tàu bị theo dõi
    useEffect(() => {
        if (contentType == "tracker") {
            if (vesselBeingTrackedId && vesselBeingTrackedId != "") {
                //vẽ đường đi nếu đang theo dõi
                if (trackPath.current) {
                    currentMapRef.current?.removeLayer(trackPath.current);
                }
                if (trackPointIconLayer.current) {
                    currentMapRef.current?.removeLayer(trackPointIconLayer.current);
                    trackPointIconLayer.current.clearLayers();
                }
                var track: any[] = [];
                track = vessels[vesselBeingTrackedId].positions.filter((x: any) => {
                    const time = x.arrivedtime;
                    return time >= currentPositionDataRange[0] && time <= currentPositionDataRange[1];
                });
                //loại bỏ các điểm ngoài vùng hiển thị
                trackPath.current = L.polyline(track.filter(item => mapBounds.contains(L.latLng(item.latitude, item.longitude)))
                    .map(item => [item.latitude, item.longitude]), {
                    color: '#9d0300', weight: 2
                }).addTo(currentMapRef.current!);
                for (let i = 0; i < track.length; i++) {
                    if (mapBounds.contains(L.latLng(track[i].latitude, track[i].longitude))) {
                        let position: L.LatLng = L.latLng(track[i].latitude, track[i].longitude);
                        let icon = L.marker(position, {
                            icon: L.divIcon({
                                html: positionIcon[track[i].type as keyof typeof positionIcon] ?? positionIcon.POS,
                                className: "trackpoint-icon",
                                iconSize: [20, 20],
                                iconAnchor: [10, 10],
                            }),
                            interactive: true,
                        });
                        icon.on('click', function () {
                            setChosenPosition(position);
                        });
                        trackPointIconLayer.current?.addLayer(icon);
                    }
                }
                if (currentMapRef.current!.getZoom() > rankZoom["track-point"].minZoom) trackPointIconLayer.current?.addTo(currentMapRef.current!);
            } else {
                //xóa đường đi và icon điểm
                if (trackPath.current) {
                    currentMapRef.current?.removeLayer(trackPath.current);
                    trackPath.current = L.polyline([]);
                }
                if (trackPointIconLayer.current) {
                    currentMapRef.current?.removeLayer(trackPointIconLayer.current);
                    trackPointIconLayer.current.clearLayers();
                }
            }
        }else{
            //xóa đường đi và icon điểm
            if (trackPath.current) {
                currentMapRef.current?.removeLayer(trackPath.current);
                trackPath.current = L.polyline([]);
            }
            if (trackPointIconLayer.current) {
                currentMapRef.current?.removeLayer(trackPointIconLayer.current);
                trackPointIconLayer.current.clearLayers();
            }
        }
    }, [vesselBeingTrackedId, currentPositionDataRange, vessels, contentType]);
    // gắn sự kiện chức năng theo dõi vị trí tàu thuyền cho marker
    useEffect(() => {
        const handleMarkerClick = (vesselId: string) => {
            setVesselBeingTrackedId(vesselId);
        };
        const handleZoom = () => {
            const z = currentMapRef.current!.getZoom();
            if (trackPointIconLayer.current.getLayers().length > 0 && z > rankZoom["track-point"].minZoom) {
                if (!currentMapRef.current!.hasLayer(trackPointIconLayer.current)) currentMapRef.current!.addLayer(trackPointIconLayer.current);
                if (highlightIcon.current.getLatLng().lat != 0 && highlightIcon.current.getLatLng().lng != 0) {
                    if (!currentMapRef.current!.hasLayer(highlightIcon.current)) currentMapRef.current!.addLayer(highlightIcon.current);
                }
            } else {
                currentMapRef.current!.removeLayer(trackPointIconLayer.current);
                currentMapRef.current!.removeLayer(highlightIcon.current);
            }
        };
        if (contentType == "tracker") {
            //gắn sự kiện cho từng marker
            let layerGroup;
            if (currentMapRef.current == leafletMapRef.current) {
                layerGroup = leafletClusterGroup.current;
            } else {
                layerGroup = windyClusterGroup.current;
            }
            layerGroup.eachLayer((layer) => {
                if (layer instanceof L.Marker) {
                    const vesselId = (layer.options as any).id;
                    (layer.options as any)._customClickHandler = () => handleMarkerClick(vesselId);
                    layer.on('click', (layer.options as any)._customClickHandler);
                }
            });
            currentMapRef.current?.on('zoomend', handleZoom);
        } else {
            //xoá sự kiện click trên marker
            let layerGroup;
            if (currentMapRef.current == leafletMapRef.current) layerGroup = leafletClusterGroup.current;
            else layerGroup = windyClusterGroup.current;
            layerGroup.eachLayer((layer) => {
                if (layer instanceof L.Marker && (layer.options as any)._customClickHandler) {
                    layer.off('click', (layer.options as any)._customClickHandler);
                }
            });
            currentMapRef.current?.off('zoomend', handleZoom);
        }
    }, [contentType]);
    //thêm icon highlight cho vị trí
    useEffect(() => {
        if (currentMapRef.current!.getZoom() <= rankZoom["track-point"].minZoom) return;
        if (chosenPosition.lat != 0 && chosenPosition.lng != 0) {
            if (!currentMapRef.current?.hasLayer(highlightIcon.current)) highlightIcon.current.addTo(currentMapRef.current!);
            highlightIcon.current.setLatLng(chosenPosition);
        } else {
            if (currentMapRef.current?.hasLayer(highlightIcon.current)) currentMapRef.current?.removeLayer(highlightIcon.current);
        }
    }, [currentMapRef.current, chosenPosition]);
    //thêm hoặc bỏ vòng tròn tạm thời trên bản đồ, thêm sự kiện cho vòng tròn
    useEffect(() => {
        if (contentType == "warningZoneCreateForm") {
            if (!currentMapRef.current?.hasLayer(draggableCircle.current)) {
                draggableCircle.current.addTo(currentMapRef.current!);
                draggableCircle.current.on('dragend', function (e) {
                    let { lat, lng } = e.target.getLatLng();
                    // Giới hạn trong mapBounds
                    if (!mapBounds.contains([lat, lng])) {
                        const bounded = mapBounds.pad(-0.01);
                        lat = Math.max(bounded.getSouth(), Math.min(lat, bounded.getNorth()));
                        lng = Math.max(bounded.getWest(), Math.min(lng, bounded.getEast()));
                        e.target.setLatLng([lat, lng]);
                    }
                    setDraggableCirclePosition(L.latLng(lat, lng));
                });
            }
        } else if (contentType == "warningZoneUpdateForm") {
            if (warningZoneBeingEditedId && warningZones[warningZoneBeingEditedId]) {
                const zone = warningZones[warningZoneBeingEditedId];
                draggableCircle.current.setLatLng([zone.centerLat, zone.centerLon]);
                draggableCircle.current.setRadius(zone.radius);
                if (!currentMapRef.current?.hasLayer(draggableCircle.current)) {
                    draggableCircle.current.addTo(currentMapRef.current!);
                }
                draggableCircle.current.on('dragend', function (e) {
                    let { lat, lng } = e.target.getLatLng();
                    // Giới hạn trong mapBounds
                    if (!mapBounds.contains([lat, lng])) {
                        const bounded = mapBounds.pad(-0.01);
                        lat = Math.max(bounded.getSouth(), Math.min(lat, bounded.getNorth()));
                        lng = Math.max(bounded.getWest(), Math.min(lng, bounded.getEast()));
                        e.target.setLatLng([lat, lng]);
                    }
                    setDraggableCirclePosition(L.latLng(lat, lng));
                });
            }
        } else {
            if (currentMapRef.current?.hasLayer(draggableCircle.current)) {
                currentMapRef.current.removeLayer(draggableCircle.current);
            }
            draggableCircle.current.off('dragend');
            draggableCircle.current.off('drag');
        }
    }, [contentType, warningZoneBeingEditedId]);
    //thêm action cho map
    useEffect(() => {
        const setView = (lat: number | undefined, lng: number | undefined, zoom: number | undefined) => {
            lat = lat ?? currentMapRef.current!.getCenter().lat;
            lng = lng ?? currentMapRef.current!.getCenter().lng;
            zoom = zoom ?? currentMapRef.current!.getZoom();
            currentMapRef.current?.setView([lat, lng], zoom);
        }
        const setViewToVesselMarker = (unitId: string, zoom: number) => {
            const vessel = vessels[unitId];
            if (vessel) {
                let groupMarker;
                if (currentMapRef.current == leafletMapRef.current) groupMarker = leafletClusterGroup.current;
                else groupMarker = windyClusterGroup.current;
                groupMarker.eachLayer((layer) => {
                    if (layer instanceof L.Marker && (layer.options as any).id == unitId) {
                        setView(layer.getLatLng().lat, layer.getLatLng().lng, zoom);
                    }
                });
            }
        };
        const openVesselMarkerPopup = (unitId: string) => {
            const vessel = vessels[unitId];
            if (vessel) {
                let groupMarker;
                if (currentMapRef.current == leafletMapRef.current) groupMarker = leafletClusterGroup.current;
                else groupMarker = windyClusterGroup.current;
                groupMarker.eachLayer((layer) => {
                    if (layer instanceof L.Marker && (layer.options as any).id == unitId) {
                        layer.openPopup();
                    }
                });
            }
        };
        const highlightPosition = (position: L.LatLng) => {
            if (currentMapRef.current!.getZoom() <= rankZoom["track-point"].minZoom) return;
            if (position.lat != 0 && position.lng != 0) {
                if (currentMapRef.current) {
                    highlightIcon.current.setLatLng(position);
                    if (!currentMapRef.current.hasLayer(highlightIcon.current)) {
                        currentMapRef.current.addLayer(highlightIcon.current);
                    }
                }
            } else {
                if (currentMapRef.current?.hasLayer(highlightIcon.current)) {
                    currentMapRef.current.removeLayer(highlightIcon.current);
                }
            }
        };
        const clickVesselMarker = (unitId: string) => {
            let groupMarker;
            if (currentMapRef.current == leafletMapRef.current) groupMarker = leafletClusterGroup.current;
            else groupMarker = windyClusterGroup.current;
                groupMarker.eachLayer((layer) => {
                if (layer instanceof L.Marker && (layer.options as any).id == unitId) {
                    layer.fire('click');
                }
            });
        };
        const clickWarningZoneMarker = (zoneId: string) => {
            let groupMarker;
            if (currentMapRef.current == leafletMapRef.current) groupMarker = warningZoneLeafletLayerGroup.current;
            else groupMarker = warningZoneWindyLayerGroup.current;
            groupMarker.eachLayer((layer) => {
                if (layer instanceof L.Circle && (layer.options as any).id == zoneId) {
                    layer.fire('click');
                }
            });
        };
        const setDraggableCircleRadius = (radius: number) => {
            draggableCircle.current.setRadius(radius);
        };
        const setDraggableCirclePosition = (lat: number, lng: number) => {
            draggableCircle.current.setLatLng([lat, lng]);
        }
        const clickDraggableCircle = () => {
            draggableCircle.current.fire('click');
        };
        const setDraggableCircleDrag = (draggable: boolean) => {
            if (draggable) {
                (draggableCircle.current as any).dragging.enable();
            } else {
                (draggableCircle.current as any).dragging.disable();
            }
        };
        setAction({ setView, setViewToVesselMarker, openVesselMarkerPopup, highlightPosition, clickVesselMarker, clickWarningZoneMarker, setDraggableCircleRadius, setDraggableCirclePosition, clickDraggableCircle, setDraggableCircleDrag });
    }, [vessels]);
    return (
        <>
            <div className={styles["windy-map"]} id="windy"></div>
            <div className={styles["leaflet-map"]} id="leaflet"></div>
        </>
    );
};

export default MapContainer;

function initializeBaseMap(map: L.Map, store: any, mapDisplay: string) {
    //tạo các basemap
    // var rasterLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    //     pane: 'tilePane'
    // });//openstreetmap
    // var vectorTileLayer = L.vectorGrid.protobuf('https://api.maptiler.com/tiles/v3-openmaptiles/{z}/{x}/{y}.pbf?key=' + apiKey["MapTiler"],
    //   {
    //     vectorTileLayerStyles: tileLayerStyles,
    //     maxNativeZoom: 14,
    //     maxZoom: 20,
    //     interactive: true,
    //     getFeatureId: (f: any) => f.properties.id//kiểm tra lại
    //   }
    // );//MapTiler
    var vectorTileLayerMapBox = L.vectorGrid.protobuf('https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/{z}/{x}/{y}.vector.pbf?access_token=' + apiKey.mapbox, {
        vectorTileLayerStyles: tileLayerStyles,
        maxNativeZoom: 20,
        maxZoom: 20,
        interactive: true,
        getFeatureId: (f: any) => f.properties.id//kiểm tra lại
    }).addTo(map); //mapbox
    // var vectorTileLayerTomTom = L.vectorGrid.protobuf(`https://api.tomtom.com/maps/orbis/map-display/tile/{z}/{x}/{y}.pbf?apiVersion=1&key=${apiKey.TomTom}&view=VN`, {
    //   vectorTileLayerStyles: tileLayerStyles,
    //   maxNativeZoom: 14,
    //   maxZoom: 20,
    //   interactive: true,
    //   getFeatureId: (f: any) => f.id,
    // });//TomTom
    // var urlBanDoNen = "https://vnsdi.mae.gov.vn/basemap/rest/services/BanDoNenTyLeNho/MapServer?token=" + key.esri;
    // const lyrBanDoNen = L.esri.tiledMapLayer({ url: urlBanDoNen, maxNativeZoom: 15, maxZoom: 20 });//esri
    // var baseMaps = {
    //   "MapTiler": vectorTileLayer,
    //   "Raster tile": rasterLayer,
    //   "Mapbox": vectorTileLayerMapBox,
    //   "TomTom": vectorTileLayerTomTom,
    // };

    // vectorTileLayer.on('click', featureInfo);
    // vectorTileLayerMapBox.on('click', featureInfo);
    // vectorTileLayerTomTom.on('click', featureInfo);
    // var layerControl = L.control.layers(baseMaps, null).addTo(map);
    //vẽ các đường biên giới
    borders.forEach(border => {
        const latLngs = border.points.map((pt: number[]) => [pt[0], pt[1]] as [number, number]);
        L.polyline(latLngs, { color: border.color, weight: 1 }).addTo(map);
    });
    //hiển thị theo mức zoom
    // var labelGroup = L.layerGroup();
    // const labelMarkers = placesLabel.map(item => {
    //   var marker = L.marker(item.latlng, {
    //     icon: L.divIcon({
    //       className: 'custom-label',
    //       html: `<span>${item.name}</span>`,
    //       iconSize: [100, 20]
    //     })
    //   });
    //   labelGroup.addLayer(marker);
    //   marker.rank = item.rank;
    //   return marker
    // });
    // labelGroup.addTo(map);
    //thêm warning zone layer
    // Object.keys(warningZones).forEach(key => {
    //   var zone = warningZones[key];
    //   if (new Date(zone.endTime) < new Date() || zone.status == 3) return;//bỏ qua vùng đã hết hạn hoặc đã huỷ
    //   else createWarningZoneCircle(key, map);
    // });
    //icon của cảng
    // const defaultIcon = L.icon({
    //     iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    //     iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    //     shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    //     // smaller size to make marker less prominent on the map
    //     iconSize: [16, 26],
    //     // anchor roughly at bottom-center of the smaller icon
    //     iconAnchor: [8, 26],
    //     // adjust popup and tooltip anchors to match smaller icon
    //     popupAnchor: [1, -22],
    //     tooltipAnchor: [8, -18],
    //     shadowSize: [26, 26]
    // });
    const portIconBlue = L.icon({
        iconUrl: portBlue,
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        // smaller size to make marker less prominent on the map
        iconSize: [16, 26],
        // anchor roughly at bottom-center of the smaller icon
        iconAnchor: [8, 26],
        // adjust popup and tooltip anchors to match smaller icon
        popupAnchor: [1, -22],
        tooltipAnchor: [8, -18],
        shadowSize: [26, 26]
    });
    const portIconYellow = L.icon({
        iconUrl: portYellow,
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        // smaller size to make marker less prominent on the map
        iconSize: [16, 26],
        // anchor roughly at bottom-center of the smaller icon
        iconAnchor: [8, 26],
        // adjust popup and tooltip anchors to match smaller icon
        popupAnchor: [1, -22],
        tooltipAnchor: [8, -18],
        shadowSize: [26, 26]
    });
    var portMarkerGroup = L.layerGroup();
    const portMarkerRankMap = new WeakMap<L.Marker, string>();
    var portMarkers = portList.map(port => {
        const icon = port.can_issue_sc ? portIconYellow : portIconBlue;
        var marker = L.marker([port.lat, port.lon], {
            icon,
            alt: port.name,
        }).bindPopup(`<p class="text">${port.name}</p>`);
        portMarkerRankMap.set(marker, 'port'); //gán rank hiển thị cho marker cảng
        portMarkerGroup.addLayer(marker);
        return marker
    });
    portMarkerGroup.addTo(map);
    //điều khiển hiển thị marker theo rank
    map.on('zoomend', () => {
        const z = map.getZoom();
        // labelMarkers.forEach(marker => {
        //   if (z < (rankZoom[marker.rank].minZoom ? rankZoom[marker.rank].minZoom : map.getMinZoom())
        //     || z > (rankZoom[marker.rank].maxZoom ? rankZoom[marker.rank].maxZoom : map.getMaxZoom())) {
        //     map.removeLayer(marker);
        //   } else {
        //     if (!map.hasLayer(marker)) {
        //       map.addLayer(marker);
        //     }
        portMarkers.forEach(marker => {
            const rank = portMarkerRankMap.get(marker);
            if (rank != undefined) {
                const rankKey = String(rank) as keyof typeof rankZoom;
                if (z < rankZoom[rankKey].minZoom
                    || z > rankZoom[rankKey].maxZoom) {
                    map.removeLayer(marker);
                } else {
                    if (!map.hasLayer(marker)) {
                        map.addLayer(marker);
                    }
                }
            }
        });
    });
    //trackPoint trackPointIcon
    // if (trackPointLayer.enable && trackPointIconLayer.enable && z > rankZoom["track-point"].minZoom) {
    //   if (!map.hasLayer(trackPointLayer)) { map.addLayer(trackPointLayer); }
    //   if (!map.hasLayer(trackPointIconLayer)) map.addLayer(trackPointIconLayer);
    // } else {
    //   map.removeLayer(trackPointLayer);
    //   map.removeLayer(trackPointIconLayer);
    // }
    // //marker tàu thuyền

    // if (map._mapName === "leaflet") {
    //   map.addLayer(leafletClusterGroup);
    // }
    // if (map._mapName === "windy") {
    //   map.addLayer(windyClusterGroup);
    // }
}
function pulseEffectOnVesselMarker(map: L.Map, latitude: number, longitude: number) {
    if (!map) return;
    var pulseSvg = ReactDOMServer.renderToString(<PulseEffectIcon />);
    // Tạo divIcon cho hiệu ứng pulse
    var pulseIcon = L.divIcon({
        className: 'vessel-pulse-icon',
        html: pulseSvg,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
    });
    // Thêm icon tại vị trí marker leaflet
    var pulseMarker = L.marker(L.latLng(latitude, longitude), { icon: pulseIcon, interactive: false });
    pulseMarker.addTo(map);
    // Xoá hiệu ứng sau 4.5s
    setTimeout(() => {
        if (pulseMarker) pulseMarker.remove();
    }, 4500);
}
function createVesselMarker(key: string, vessel: Vessel, vesselMarkerIcon: string,
    openSidebar: (contentType: SidebarContentType) => void, setVesselBeingTrackedId: (id: string) => void,
    setChatBeingViewedId: (id: string) => void): L.Marker {
    if (!vessel.positions || vessel.positions.length == 0) {
        throw new Error("Vessel has no positions");
    }
    // Tìm vị trí cuối cùng hợp lệ
    const lastValidIndex = vessel.positions
        .map((x, i) => mapBounds.contains(L.latLng(x.latitude, x.longitude)) ? i : -1)
        .filter(i => i != -1)
        .pop() ?? vessel.positions.length - 1;

    let latitude = vessel.positions.at(lastValidIndex).latitude;
    let longitude = vessel.positions.at(lastValidIndex).longitude;
    const vesselName = vessel.vesselName ?? key;
    const heading = vessel.positions.at(-1).cog;
    const positiontime = vessel.positions.at(-1).positiontime;
    const speed = vessel.positions.at(-1).sog;
    const positionType = vessel.positions.at(-1).type;

    // Nếu marker nằm ngoài bản đồ, tìm vị trí gần nhất hoặc về Hà Nội
    if (!mapBounds.contains(L.latLng(latitude, longitude))) {
        for (let i = vessel.positions.length - 2; i >= 0; i--) {
            if (mapBounds.contains(L.latLng(vessel.positions[i].latitude, vessel.positions[i].longitude))) {
                latitude = vessel.positions[i].latitude;
                longitude = vessel.positions[i].longitude;
                break;
            }
        }
        if (!mapBounds.contains(L.latLng(latitude, longitude))) {
            latitude = 21.028511;
            longitude = 105.854444;
        }
    }

    // Tạo icon
    const vesselIcon = L.divIcon({
        className: `custom-icon ${vesselColor[speed > 0 ? 6 : 5] ?? 5}`,
        html: vesselMarkerIcon, // SVG như bạn đã dùng
        iconSize: [20, 100],
        iconAnchor: [10, 50],
        popupAnchor: [0, 0]
    });
    // Tạo popup content bằng React component (nếu muốn, hoặc dùng HTML như cũ)
    const popupContainer = document.createElement("div");
    ReactDOM.createRoot(popupContainer).render(
        <VesselMarkerPopup
            vesselName={vesselName}
            keyId={key}
            latitude={latitude}
            longitude={longitude}
            speed={speed}
            heading={heading}
            positionType={positionType}
            positionTime={positiontime}
            onTrackerClick={() => {
                openSidebar("tracker");
                setVesselBeingTrackedId(key);
            }}
            onMessageClick={() => { setChatBeingViewedId(key); openSidebar("chatMessage"); }}
        />
    );

    const marker = L.marker([latitude, longitude], {
        alt: 'tàu ' + vesselName,
        icon: vesselIcon,
    }).bindPopup(popupContainer);

    // Set custom properties on the marker instance
    (marker as any).options.vesselName = vesselName;
    (marker as any).options.id = key;
    (marker as any).options.heading = heading || 0;
    (marker as any).options.rotationAngle = heading || 0;
    (marker as any).options.rotationOrigin = "center center";
    (marker as any).options.latitude = latitude;
    (marker as any).options.longitude = longitude;
    (marker as any).options.positiontime = positiontime;
    return marker;
}
function createWarningZoneCircle(warningZone: WarningZone, openSidebar: (contentType: SidebarContentType) => void,
    setWarningZoneBeingEditedId: (id: string) => void): L.Circle {
    const { id, name, centerLat, centerLon, radius, severity, auto_notify, startTime, endTime } = warningZone;
    // Xác định nhãn cấp độ và màu sắc theo mức độ severity
    let levelLabel, color, fillColor;
    switch (severity) {
        case 1:
            levelLabel = "Bình thường";
            color = '#9d5400ff';
            fillColor = '#ddff0093';
            break;
        case 2:
            levelLabel = "Mạnh";
            color = '#9d3700ff';
            fillColor = '#ffb3007c';
            break;
        case 3:
            levelLabel = "Rất mạnh";
            color = '#9d1800ff';
            fillColor = '#ff6a007d';
            break;
        case 4:
            levelLabel = "Siêu mạnh";
            color = '#9d0300';
            fillColor = '#ff00006f';
            break;
        default:
            levelLabel = "Bình thường";
            color = '#2196f3';
            fillColor = '#2196f35e';
            break;
    }

    let circle = L.circle([centerLat, centerLon], ({
        radius: radius,
        color: color,
        fillColor: fillColor,
        fillOpacity: 0.3,
        id: id,
        weight: 0.1,
    } as any));
    let popupContainer = document.createElement("div");
    ReactDOM.createRoot(popupContainer).render(
        <WarningZonePopup
            name={name}
            id={id}
            latitude={centerLat}
            longitude={centerLon}
            radius={radius}
            severity={severity}
            autoNotify={auto_notify}
            startTime={startTime}
            endTime={endTime}
            onEditClick={() => {
                setWarningZoneBeingEditedId(id);
                openSidebar("warningZoneUpdateForm");
            }}
            onMessageClick={() => {
                setWarningZoneBeingEditedId(id);
                openSidebar("warningZoneChatMessage");
            }}
            onVesselInsideClick={() => {
                setWarningZoneBeingEditedId(id);
                openSidebar("warningZoneVesselList");
            }}
        />
    );
    circle.bindPopup(popupContainer);
    return circle;
}